import { StyleSheet } from 'react-native';

import { ButtonPalette as Palette, ButtonSize as Size } from '../Button.types';
import { COLORS, OPACITY } from '@garnish/constants';

/**
 * The styles are organized by component type: Box, Text, Icon
 *
 * Each component's stylesheet has styles for the disabled-state-- and then
 * palette-specific groups, with definitions for both standard + hovered/pressed
 *
 * These styles are highly structured because there's mapping complexity which
 * relies on a standardized signature.
 */

const PRESSED = 'pressed';
const DISABLED = 'disabled';

// -----------------------------------------------------------------------------
// ---- BOX STYLES -------------------------------------------------------------
// -----------------------------------------------------------------------------

export const boxStyles = StyleSheet.create({
  // -----------------------------------------
  // ---- DISABLED ---------------------------
  [DISABLED]: { backgroundColor: COLORS.OPACITY.GREEN_1.LIGHTEST },

  // -----------------------------------------
  // ---- PRIMARY ----------------------------
  [`${Palette.primary}`]: { backgroundColor: COLORS.GREEN_2 },
  [`${Palette.primary}:${PRESSED}`]: { backgroundColor: COLORS.GREEN_3 },

  // -----------------------------------------
  // ---- PRIMARY OUTLINE --------------------
  [`${Palette.primaryOutline}`]: {
    backgroundColor: COLORS.OPACITY.GREEN_2.LIGHTEST,
    borderColor: COLORS.KALE,
    borderWidth: 1,
  },
  [`${Palette.primaryOutline}:${PRESSED}`]: {
    backgroundColor: COLORS.OPACITY.GREEN_2.LIGHTER,
    borderColor: COLORS.KALE,
    borderWidth: 1,
  },

  // -----------------------------------------
  // ---- SECONDARY --------------------------
  [`${Palette.secondary}`]: {
    backgroundColor: OPACITY.TRANSPARENT,
    borderColor: COLORS.GREEN_1,
    borderWidth: 1,
  },
  [`${Palette.secondary}:${PRESSED}`]: {
    backgroundColor: OPACITY.GREEN_2.LIGHTER,
    borderColor: COLORS.GREEN_2,
    borderWidth: 1,
  },

  // -----------------------------------------
  // ---- SECONDARY WITH CAUTION HOVER -------
  [`${Palette.secondaryWithCautionHover}`]: {
    backgroundColor: OPACITY.TRANSPARENT,
    borderColor: COLORS.GREEN_2,
    borderWidth: 1,
  },
  [`${Palette.secondaryWithCautionHover}:${PRESSED}`]: {
    backgroundColor: OPACITY.TRANSPARENT,
    borderColor: COLORS.CAUTION,
    borderWidth: 1,
  },

  // -----------------------------------------
  // ---- LIGHT ------------------------------
  [`${Palette.light}`]: { backgroundColor: COLORS.NEUTRAL_7 },
  [`${Palette.light}:${PRESSED}`]: { backgroundColor: COLORS.NEUTRAL_7 },
  [`${Palette.light}:${DISABLED}`]: { backgroundColor: COLORS.NEUTRAL_7 },

  // -----------------------------------------
  // ---- MUTED ------------------------------
  [`${Palette.muted}`]: {
    backgroundColor: OPACITY.GREEN_1.ALMOST_TRANSPARENT,
    borderColor: COLORS.OPACITY.TRANSPARENT,
    borderWidth: 1,
  },
  [`${Palette.muted}:${PRESSED}`]: {
    backgroundColor: OPACITY.GREEN_1.LIGHTEST,
    borderColor: COLORS.OPACITY.TRANSPARENT,
    borderWidth: 1,
  },

  // -----------------------------------------
  // ---- INVERTED ---------------------------
  [`${Palette.inverted}`]: { backgroundColor: COLORS.KHAKI_3 },
  [`${Palette.inverted}:${PRESSED}`]: { backgroundColor: COLORS.GREEN_5 },

  // -----------------------------------------
  // ---- CAUTION ----------------------------
  [`${Palette.caution}`]: {
    borderColor: COLORS.CAUTION,
    borderWidth: 1,
    backgroundColor: OPACITY.TRANSPARENT,
  },
  [`${Palette.caution}:${PRESSED}`]: {
    borderColor: COLORS.CAUTION,
    borderWidth: 1,
    backgroundColor: OPACITY.CAUTION.LIGHTER,
  },

  [`${Palette.outlinedLight}`]: {
    backgroundColor: COLORS.SPINACH,
    borderWidth: 1,
    borderColor: COLORS.WHITE,
  },
  [`${Palette.outlinedLight}:${PRESSED}`]: {
    backgroundColor: COLORS.KALE,
    borderWidth: 1,
    borderColor: COLORS.WHITE,
  },
  [`${Palette.outlinedLight}:${DISABLED}`]: {
    backgroundColor: COLORS.AVOCADO,
    borderWidth: 1,
    borderColor: COLORS.WHITE,
  },

  // -----------------------------------------
  // ---- LIME -------------------------------
  [`${Palette.lime}`]: { backgroundColor: COLORS.LIME },
  [`${Palette.lime}:${PRESSED}`]: { backgroundColor: COLORS.LIME },
  [`${Palette.lime}:${DISABLED}`]: { backgroundColor: COLORS.LIME },
  // ---- LIME OUTLINE --------------------
  [`${Palette.limeOutline}`]: {
    backgroundColor: COLORS.GREEN_6,
    borderColor: COLORS.GREEN_2,
    borderWidth: 1,
  },
  [`${Palette.limeOutline}:${PRESSED}`]: {
    backgroundColor: COLORS.GREEN_6_HOVER,
    borderColor: COLORS.GREEN_2,
    borderWidth: 1,
  },

  // -----------------------------------------
  // ---- LIME ON GREEN --------------------
  [`${Palette.limeOnGreen}`]: {
    backgroundColor: COLORS.GREEN_2,
  },
  [`${Palette.limeOnGreen}:${PRESSED}`]: {
    backgroundColor: COLORS.GREEN_3,
  },

  // -----------------------------------------
  // ---- QUINOA ----------------------------
  [`${Palette.quinoa}`]: {
    borderColor: COLORS.DARK_KALE,
    borderWidth: 1,
    backgroundColor: COLORS.QUINOA,
  },
  [`${Palette.quinoa}:${PRESSED}`]: {
    borderColor: COLORS.DARK_KALE,
    borderWidth: 1,
    backgroundColor: COLORS.QUINOA_HOVER,
  },
  [`${Palette.quinoa}:${DISABLED}`]: {
    borderColor: COLORS.DARK_KALE,
    borderWidth: 1,
    backgroundColor: COLORS.QUINOA,
  },

  // -----------------------------------------
  // ---- SIZES ------------------------------
  [Size.xsmall]: { paddingHorizontal: 12, height: 24 },
  [Size.small]: { paddingHorizontal: 12, height: 32 },
  [Size.medium]: { paddingHorizontal: 24, height: 40 },
  [Size.large]: { paddingHorizontal: 24, height: 48 },
  [Size.xlarge]: { paddingHorizontal: 32, height: 100 },
});

// -----------------------------------------------------------------------------
// ---- TEXT AND ICON STYLES ---------------------------------------------------
// -----------------------------------------------------------------------------

const SHARED_TEXT_COLOR_STYLES = {
  // -----------------------------------------
  // ---- DISABLED ---------------------------
  disabled: { color: COLORS.OPACITY.GREEN_1.DARK },

  // -----------------------------------------
  // ---- PRIMARY ----------------------------
  [`${Palette.primary}`]: { color: COLORS.WHITE },
  [`${Palette.primary}:${PRESSED}`]: { color: COLORS.WHITE },

  // -----------------------------------------
  // ---- PRIMARY OUTLINE --------------------
  [`${Palette.primaryOutline}`]: { color: COLORS.KALE },
  [`${Palette.primaryOutline}:${PRESSED}`]: { color: COLORS.KALE },

  // -----------------------------------------
  // ---- SECONDARY --------------------------
  [`${Palette.secondary}`]: { color: COLORS.TEXT_COLOR },
  [`${Palette.secondary}:${PRESSED}`]: { color: COLORS.TEXT_COLOR },

  // -----------------------------------------
  // ---- SECONDARY WITH CAUTION HOVER -------
  [`${Palette.secondaryWithCautionHover}`]: { color: COLORS.TEXT_COLOR },
  [`${Palette.secondaryWithCautionHover}:${PRESSED}`]: {
    color: COLORS.CAUTION,
  },

  // -----------------------------------------
  // ---- LIGHT ------------------------------
  [`${Palette.light}`]: { color: COLORS.TEXT_COLOR },
  [`${Palette.light}:${PRESSED}`]: { color: COLORS.GREEN_3 },
  [`${Palette.light}:${DISABLED}`]: { color: COLORS.OPACITY.GREEN_1.LIGHT },

  // -----------------------------------------
  // ---- MUTED ------------------------------
  [`${Palette.light}`]: { color: COLORS.TEXT_COLOR },
  [`${Palette.light}:${PRESSED}`]: { color: COLORS.KALE },
  [`${Palette.light}:${DISABLED}`]: { color: COLORS.OPACITY.GREEN_1.LIGHT },

  // -----------------------------------------
  // ---- INVERTED ---------------------------
  [`${Palette.inverted}`]: { color: COLORS.TEXT_COLOR },
  [`${Palette.inverted}:${PRESSED}`]: { color: COLORS.TEXT_COLOR },

  // -----------------------------------------
  // ---- CAUTION ----------------------------
  [`${Palette.caution}`]: { color: COLORS.CAUTION },
  [`${Palette.caution}:${PRESSED}`]: { color: COLORS.CAUTION },

  [`${Palette.outlinedLight}`]: { color: COLORS.WHITE },
  [`${Palette.outlinedLight}:${PRESSED}`]: { color: COLORS.WHITE },
  [`${Palette.outlinedLight}:${DISABLED}`]: { color: COLORS.WHITE },

  // -----------------------------------------
  // ---- LIME -------------------------------
  [`${Palette.lime}`]: { color: COLORS.KALE },
  [`${Palette.lime}:${PRESSED}`]: { color: COLORS.KALE },

  // -----------------------------------------
  // ---- LIME ON GREEN -------------------------------
  [`${Palette.limeOnGreen}`]: { color: COLORS.LIME },
  [`${Palette.limeOnGreen}:${PRESSED}`]: { color: COLORS.LIME },

  // -----------------------------------------
  // ---- QUINOA ----------------------------
  [`${Palette.quinoa}`]: { color: COLORS.TEXT_COLOR },
  [`${Palette.quinoa}:${PRESSED}`]: { color: COLORS.TEXT_COLOR },
};

export const textStyles = StyleSheet.create({
  ...SHARED_TEXT_COLOR_STYLES,

  // -----------------------------------------
  // ---- SIZES ------------------------------
  [Size.xsmall]: { fontSize: 12 },
  [Size.small]: { fontSize: 14 },
  [Size.medium]: { fontSize: 14 },
  [Size.large]: { fontSize: 16 },
  [Size.xlarge]: { fontSize: 24 },
});

export const iconStyles = StyleSheet.create({
  ...SHARED_TEXT_COLOR_STYLES,

  // -----------------------------------------
  // ---- SIZES ------------------------------
  [Size.xsmall]: { width: 16 },
  [Size.small]: { width: 16 },
  [Size.medium]: { width: 16 },
  [Size.large]: { width: 24 },
  [Size.xlarge]: { width: 32 },
});
